import Phaser from "phaser";
import type { GameTheme } from "../themes/types";
import { darkenColor } from "../utils/colors";
import { drawProgressBar } from "../utils/graphics";

export interface ProgressBarConfig {
  x: number;
  y: number;
  width: number;
  height?: number;
  progress: number; // 0-1
  theme: GameTheme;
  fillColor?: number;
  emptyColor?: number;
  showStars?: boolean;
  starPositions?: number[]; // Array of progress values (0-1) where stars appear
}

export class ProgressBar extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.Graphics;
  private stars: Phaser.GameObjects.Graphics[] = [];
  private config: Required<ProgressBarConfig>;
  private currentProgress: number;

  constructor(scene: Phaser.Scene, config: ProgressBarConfig) {
    super(scene, config.x, config.y);

    this.config = {
      ...config,
      height: config.height ?? 32,
      fillColor: config.fillColor ?? config.theme.progressFill,
      emptyColor: config.emptyColor ?? config.theme.progressEmpty,
      showStars: config.showStars ?? false,
      starPositions: config.starPositions ?? [0.33, 0.66, 1.0],
    };

    this.currentProgress = Math.max(0, Math.min(1, config.progress));

    this.createProgressBar();

    if (this.config.showStars) {
      this.createStars();
    }

    scene.add.existing(this);
  }

  private createProgressBar(): void {
    this.background = this.scene.add.graphics();
    this.add(this.background);

    this.drawBar();
  }

  private drawBar(): void {
    const { width, height, fillColor, emptyColor } = this.config;
    const borderColor = darkenColor(fillColor!, 30);

    this.background.clear();

    drawProgressBar(
      this.background,
      -width / 2,
      -height / 2,
      width,
      height,
      this.currentProgress,
      emptyColor!,
      fillColor!,
      borderColor,
      3
    );
  }

  private createStars(): void {
    const { width, starPositions, theme } = this.config;

    starPositions!.forEach((position) => {
      const star = this.scene.add.graphics();
      const x = -width / 2 + width * position;
      const y = 0;

      // Draw star shape
      this.drawStar(star, x, y, 12, theme.starFilled);

      this.add(star);
      this.stars.push(star);
    });
  }

  private drawStar(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    radius: number,
    color: number
  ): void {
    const points = 5;
    const innerRadius = radius * 0.4;
    const angle = Math.PI / points;

    graphics.fillStyle(0x000000);
    graphics.lineStyle(2, 0x000000);

    graphics.beginPath();

    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? radius : innerRadius;
      const a = angle * i - Math.PI / 2;

      const px = x + Math.cos(a) * r;
      const py = y + Math.sin(a) * r;

      if (i === 0) {
        graphics.moveTo(px, py);
      } else {
        graphics.lineTo(px, py);
      }
    }

    graphics.closePath();
    graphics.strokePath();
    graphics.fillPath();

    // Fill star
    graphics.fillStyle(color);
    graphics.beginPath();

    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? radius - 2 : innerRadius;
      const a = angle * i - Math.PI / 2;

      const px = x + Math.cos(a) * r;
      const py = y + Math.sin(a) * r;

      if (i === 0) {
        graphics.moveTo(px, py);
      } else {
        graphics.lineTo(px, py);
      }
    }

    graphics.closePath();
    graphics.fillPath();
  }

  public setProgress(progress: number, animate: boolean = true): void {
    const newProgress = Math.max(0, Math.min(1, progress));

    if (animate) {
      this.scene.tweens.add({
        targets: this,
        currentProgress: newProgress,
        duration: 300,
        ease: "Power2",
        onUpdate: () => {
          this.drawBar();
        },
      });
    } else {
      this.currentProgress = newProgress;
      this.drawBar();
    }
  }

  public getProgress(): number {
    return this.currentProgress;
  }

  public destroy(): void {
    this.background.destroy();
    this.stars.forEach((star) => star.destroy());
    super.destroy();
  }
}
